import React, { useState, useRef, useEffect } from "react";
import Modal from "components/generic/Modal";
import styled from "style/styled-components";
import { City } from "api/models/City";
import { getAllCities } from "api/resources";
import useQuery from "common/hooks/useQuery";
import { DownArrowIcon } from "components/topBar/HeaderComponents";
import useLoginState from "hooks/useLoginState";
import { capitalizeFirstLetter } from "stringUtils";
import { useHistory } from "react-router-dom";
import { mobileHeaderWidthMax } from "style/breakpoints";
import { getHomePageUrl } from "core/navigation";
import loginState from "state/singletons/loginState";
import { debounce } from "lodash";

const MenuModal = styled(Modal)<{
  top: number;
  right: number;
}>`
  top: ${({ theme }) => theme.globalComponents.headerHeight - 8}px;
  right: ${({ right }) => right - 8}px;
  padding: 8px;
  @media (max-width: ${mobileHeaderWidthMax}px) {
    right: 0;
    top: ${({ top }) => top + 16}px;
    padding: 8px;
  }
`;

const CityToggle = styled.a`
  cursor: pointer;
  margin: 20px;
  display: flex;
  align-items: center;
  position: relative;
`;

export const ActiveCityLabel = styled.span`
  color: ${({ theme }) => theme.color.font.onDarkBackground};
`;

export const CitySelectionLabel = styled.span`
  display: inline-block;
  padding: 8px 0 8px 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.foreground.primary};
`;

const CitySelection = () => {
  const [citySelectionVisible, setCitySelectionVisible] = useState(false);
  const {
    getCity,
    setActiveCityScope,
    getActiveCityScope,
    setDefaultBusinessId,
    setDefaultBusinessName,
  } = useLoginState();
  const history = useHistory();
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const cityToggleRef = useRef<HTMLAnchorElement>(null);

  const isAllCitiesAdmin = getCity() === "ALL";

  if (!isAllCitiesAdmin) {
    return null;
  }

  useEffect(() => {
    const activeCityScope = getActiveCityScope();
    if (activeCityScope) {
      setDefaultBusinessId(activeCityScope.defaultBusinessId);
      setDefaultBusinessName(activeCityScope.defaultBusinessName);
    }
  }, [getActiveCityScope()]);

  const { result: cities = [] } = useQuery<City[]>({
    request: getAllCities,
  });

  useEffect(() => {
    const activeCityScope = getActiveCityScope();
    if (!activeCityScope && cities) {
      const defultCity = cities.find(city => city.city === "WUPPERTAL");
      if (defultCity) {
        setActiveCityScope(defultCity);
      }
    }
  }, [cities, getActiveCityScope]);

  const calculateMenuPosition = () => {
    if (cityToggleRef.current) {
      const rect = cityToggleRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      setMenuPosition({ top: rect.top, right: viewportWidth - rect.right });
    }
  };

  const onActiveCityLabelClick = () => {
    calculateMenuPosition();
    setCitySelectionVisible(!citySelectionVisible);
  };

  const onCitySelectionModalHide = () => {
    setCitySelectionVisible(false);
  };

  const handleCitySelection = (city: City) => {
    setActiveCityScope(city);
    setCitySelectionVisible(false);

    if (city !== getActiveCityScope()) {
      if (window.location.pathname.includes("/inhalt/partner/")) {
        history.push(getHomePageUrl(loginState));
      } else {
        window.location.reload();
      }
    }
  };

  return (
    <>
      <CityToggle ref={cityToggleRef} onClick={onActiveCityLabelClick}>
        <ActiveCityLabel>
          {capitalizeFirstLetter(getActiveCityScope()?.city || "WUPPERTAL")}
        </ActiveCityLabel>
        <DownArrowIcon />
      </CityToggle>
      {citySelectionVisible && (
        <MenuModal
          isVisible={citySelectionVisible}
          onClose={onCitySelectionModalHide}
          transparentBackdrop
          right={menuPosition.right}
          top={menuPosition.top}
        >
          {cities.map(city => (
            <CitySelectionLabel
              key={city.abbreviation}
              onClick={() => handleCitySelection(city)}
            >
              {city.name}
            </CitySelectionLabel>
          ))}
        </MenuModal>
      )}
    </>
  );
};

export default CitySelection;
